import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {MatTooltipModule} from '@angular/material/tooltip';
import {AuthenticationService, LoginProvider} from '../authentication.service';
import {MigrationHttpService} from './migration-http.service';
import {InvitationPreview, NativeRedeemMode} from './migration.models';
import {AppRoutes} from '../../enums/routes';
import {roleDescription as describeRole} from '../context/context.models';
import {describeNativeAccountError} from '../local/local-account-errors';
import {ViewportService} from '../../_services/viewport.service';
import {OrDividerComponent} from '../or-divider.component';
import {PASSWORD_POLICY_HINT} from '../../_shared/password-policy';

/**
 * Which step of the native door is on screen. See {@link InviteLandingComponent.step}.
 *
 *  * `email` — the address, and nothing else.
 *  * `password` — the password for that address, in the shape the lookup said it needs.
 *  * `confirm` — the one-time code mailed to a bound address, when the invitation owes one.
 */
export type InviteNativeStep = 'email' | 'password' | 'confirm';

/**
 * The page an invite link lands on. Shows what is being offered, then sends the invitee through the provider to
 * accept it.
 *
 * Anonymous by necessity: since #279 an account is created BY redeeming, so requiring a session here would be
 * circular — this is the entry point for people who have no account at all. It is also the flow that replaced
 * the old "sign in role-lessly, then paste your code" bootstrap.
 *
 * The roles on offer carry the SAME {@link roleDescription} sentences the issuing screen shows — what somebody
 * is about to accept and what the administrator thought they were handing over must not be two different
 * descriptions. As on the issuing screen, exactly one of the two delivery mechanisms is live at a time and
 * {@link ViewportService.isCompact} decides which — see the note on {@link roleDescription} below.
 *
 * ### A constraint the invitee cannot discover any other way (#568)
 *
 * An invitation can be issued with its address binding **enforced**: only an account that proves the bound
 * address may redeem it. That is decided by the issuer, before the link is sent, and on the federated doors the
 * invitee has no way to satisfy it retroactively — an Apple "Hide My Email" sign-in reports a relay address the
 * provider does not vouch for, and no amount of retrying changes that. So this page states the rule up front and
 * names which path is actually in use, rather than letting a refusal be the first news of it.
 *
 * ### The native door asks for the address first (#583)
 *
 * It used to ask for an intention — a segmented "Ich habe schon ein Konto" / "Neues Konto" — and then for both
 * fields at once. That put the one question this page cannot answer in front of the person least able to answer
 * it: whether the address they are about to type already has a CheqMe account is a fact about the server, not
 * about what they intend, and somebody invited to a company they have never heard of has no way of knowing
 * whether an old account of theirs is the same one. Choosing wrong was not harmless either, since the two halves
 * of the form differ in what they ask for and what the button then claims to do.
 *
 * So the address is asked for on its own, and {@link AuthenticationService.lookupInvitationRedemption} answers
 * the question that decides the second step:
 *
 *  * **the address has a native account** — one password field, and the button says "Anmelden".
 *  * **it has none** — a password plus its confirmation and the policy, and the button says "Konto anlegen".
 *
 * The server's behaviour is unchanged: one endpoint, which creates or verifies. What changed is that the
 * sentence in front of it is now derived rather than guessed, and that nobody is asked to confirm a password
 * they already have.
 *
 * {@link InvitationPreview.nativeRedeem} then decides how much this door has to ask for on top:
 *
 *  * **`open`** — nothing extra. The invitation code is the whole secret, as it was before #568.
 *  * **`matchRequired`** — the invitation was mailed to the bound address, so reading it already proved the
 *    mailbox. The address field is prefilled and locked, because any other value is a guaranteed refusal.
 *  * **`confirmationRequired`** — the enforced invitation was handed over rather than mailed, so nothing yet ties
 *    the person holding it to that mailbox. Submitting the password asks the server to mail a one-time code
 *    ({@link startConfirmation}) and the form is replaced by {@link step} `confirm` until it is typed back.
 *
 * The mode is read as **advice**, never as authorization: `InvitationEmailGate` decides at redeem time either
 * way. What it buys is that nobody fills in a password before being told there was a step they had no way of
 * knowing about.
 */
@Component({
  selector: 'auth-invite-landing',
  standalone: true,
  imports: [FormsModule, MatTooltipModule, OrDividerComponent, RouterLink],
  template: `
    <div class="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-sm">

        @if (loading) {
          <p class="py-8 text-center text-sm text-gray-400">Einladung wird geprüft …</p>
        } @else if (invalid) {
          <h1 class="text-lg font-semibold text-gray-900">Einladung ungültig</h1>
          <p class="mt-2 text-sm text-gray-600">
            Dieser Link ist abgelaufen, wurde bereits eingelöst oder zurückgezogen. Bitten Sie die Person, die
            Sie eingeladen hat, um eine neue Einladung.
          </p>
        } @else if (invitation) {
          <h1 class="text-lg font-semibold text-gray-900">Sie wurden zu CheqMe eingeladen</h1>

          <!-- The issuer's „Notiz" is NOT rendered here, and the preview no longer carries it. It is an internal
               filing note the issuing screen promises is „nur für Sie sichtbar" — „Steuerbüro Meier", „Aushilfe
               Winter" — written for the administrator's own list, and this page's reader is the person it is
               about. The strip is server-side (RoleGrantService.DescribeInvitation), so this is belt and braces:
               do not reintroduce a label binding here even if a future DTO grows the field back. -->

          <!-- WHICH tenant, above the permissions: the roles alone say what you would be able to do but not
               where, and somebody holding invitations from several companies cannot tell them apart otherwise.
               A shard-scoped invitation names both, because a location belongs to a company. -->
          @if (invitation.companyName) {
            <div class="mt-4 rounded-lg border-[1px] border-theme-primary-100 bg-theme-primary-50 p-3">
              <p class="text-xs font-semibold text-theme-primary-900">Sie werden eingeladen zu</p>
              <p class="mt-0.5 text-sm font-semibold text-gray-900">{{ invitation.companyName }}</p>
              @if (invitation.shardName) {
                <p class="text-sm text-gray-600">Standort: {{ invitation.shardName }}</p>
              }
            </div>
          }

          <!-- A labelled box is a promise that something is inside it. An employee-link invitation carries ZERO
               roles by design (ADR #260 §2b: it confers a binding, not a role), so rendering this block
               unconditionally showed every such invitee a heading called "Ihre Berechtigungen" over nothing at
               all — which reads as "the invitation is broken" rather than "this invitation is about something
               else". Each branch below says what the invitation actually confers, and the last says nothing at
               all rather than saying it emptily. -->
          @if (invitation.roles.length) {
            <div class="mt-4 rounded-lg border-[1px] border-gray-100 bg-gray-50 p-3">
              <p class="text-xs font-semibold text-gray-700">Ihre Berechtigungen</p>
              <ul class="mt-1 space-y-1.5">
                @for (role of invitation.roles; track role) {
                  <li [class.cursor-help]="!compact() && !!roleDescription(role)"
                      [matTooltip]="roleDescription(role)" [matTooltipDisabled]="compact()"
                      matTooltipClass="prose-tooltip" matTooltipPosition="above">
                    <span class="text-sm text-gray-600">{{ roleLabel(role) }}</span>
                    @if (compact() && roleDescription(role)) {
                      <span class="block text-xs text-gray-500">{{ roleDescription(role) }}</span>
                    }
                  </li>
                }
              </ul>
            </div>
          } @else if (invitation.employeeId) {
            <div class="mt-4 rounded-lg border-[1px] border-gray-100 bg-gray-50 p-3">
              <p class="text-xs font-semibold text-gray-700">Was diese Einladung Ihnen gibt</p>
              <p class="mt-1 text-sm text-gray-600">
                Zugang zu Ihrem eigenen Mitarbeiterkonto@if (invitation.employeeName) {
                  <span> als <span class="font-semibold">{{ invitation.employeeName }}</span></span>
                }@if (invitation.companyName) {
                  <span> bei <span class="font-semibold">{{ invitation.companyName }}</span></span>
                }.
              </p>
              <p class="mt-1 text-xs text-gray-500">
                Sie erhalten damit keine Verwaltungsrechte. Sie sehen nur Ihren eigenen Dienstplan, Ihre Zeiten
                und Ihre Formulare.
              </p>
            </div>
          }

          <!-- The binding, in the strength it was issued at and on the path actually in use (#581).
               This used to be an amber alert shouting the whole rule at every enforced invitation. That was the
               right instinct for the federated doors and wrong for the native one, where #580 gives the invitee
               a way to satisfy the rule from this very page: an alarm over a step somebody is about to take
               anyway reads as a problem rather than as an instruction. So it is a calm note, it names which of
               the three paths this invitation puts them on, and the one genuinely unsatisfiable case (a provider
               account whose address is not confirmed) is stated once, plainly, at the end.

               Address step only. Every one of its sentences is about the field directly below it, and the two
               later steps have no address field to point at while naming the address themselves (#586). -->
          @if (invitation.boundEmail && step === 'email') {
            <div class="mt-3 rounded-lg border-[1px] border-gray-200 bg-gray-50 p-3" role="note">
              <p class="text-xs font-semibold text-gray-800">
                Diese Einladung gilt für {{ invitation.boundEmail }}
              </p>
              <p class="mt-1 text-xs text-gray-600">
                @switch (nativeRedeem) {
                  @case ('matchRequired') {
                    Wir haben sie an diese Adresse gesendet. Melden Sie sich unten mit genau dieser Adresse an.
                  }
                  @case ('confirmationRequired') {
                    Tragen Sie unten genau diese Adresse ein. Sie erhalten dann einen Bestätigungscode an diese
                    Adresse.
                  }
                  @default {
                    Melden Sie sich mit dem Konto an, das zu dieser Adresse gehört.
                  }
                }
              </p>
              @if (invitation.enforceBoundEmail) {
                <p class="mt-1 text-xs text-gray-600">
                  Bei Microsoft, Apple oder Google muss diese Adresse dort bestätigt sein. Eine verborgene
                  Adresse wie bei Apple „E-Mail-Adresse verbergen“ wird abgewiesen.
                </p>
              }
            </div>
          }

          @if (step !== 'confirm') {
            <!-- The e-mail + password option (#279), first because it is the one way in that needs no account
                 anywhere else. Until now the only ways to accept an invitation were Microsoft, Apple and Google,
                 so somebody without one of those three could not accept at all. -->

            @if (step === 'email') {
              <!-- Step one asks for the address and nothing else (#583). Everything the second step has to
                   decide — one password field or two, which verb the button carries, whether the policy is worth
                   stating — follows from whether this address already has an account, and that is a question
                   only the server can answer. -->
              <form class="mt-5 space-y-3" (ngSubmit)="submitEmail()">
                <div>
                  <label for="invite-email" class="block text-sm font-medium text-gray-700">E-Mail-Adresse</label>
                  <!-- readonly, never disabled, when the invitation insists on this address (#580). A disabled
                       field is skipped by keyboard and form navigation and reads as broken; readonly keeps it
                       focusable and announceable while refusing the one edit that could only ever produce a
                       refusal. -->
                  <input id="invite-email" name="email" type="email" autocomplete="email" required
                         [(ngModel)]="email" [disabled]="busy" [readOnly]="emailLocked"
                         [attr.aria-describedby]="emailLocked ? 'invite-email-locked' : null"
                         class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"
                         [class.bg-gray-50]="emailLocked"/>
                  @if (emailLocked) {
                    <p id="invite-email-locked" class="mt-1 text-xs text-gray-600">
                      Diese Einladung lässt sich nur mit dieser Adresse annehmen. Sie ist deshalb festgelegt.
                    </p>
                  }
                </div>

                @if (error) {
                  <p class="text-sm text-red-700" role="alert">{{ error }}</p>
                }

                <button type="submit" [disabled]="busy || !canContinue()"
                        class="w-full rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
                  Weiter
                </button>
              </form>
            } @else {
              <!-- Step two. Which of the two shapes is on screen was decided by the server, so the heading can
                   say plainly what is about to happen instead of asking the invitee to predict it. -->
              <div class="mt-5 rounded-lg border-[1px] border-gray-200 bg-gray-50 p-3">
                <p class="text-sm text-gray-700">
                  @if (creating) {
                    Für {{ email }} gibt es noch kein CheqMe-Konto. Legen Sie jetzt eines an.
                  } @else {
                    Für {{ email }} gibt es bereits ein CheqMe-Konto. Melden Sie sich damit an.
                  }
                </p>
                <!-- Offered only while another address could still be used. On an enforced invitation every
                     other address is a guaranteed refusal, so a way back to the field would be a way back to a
                     dead end. -->
                @if (!emailLocked) {
                  <button type="button" (click)="backToEmail()" [disabled]="busy"
                          class="mt-1 text-sm font-semibold text-theme-primary-700 underline disabled:opacity-50">
                    Andere Adresse
                  </button>
                }
              </div>

              <form class="mt-3 space-y-3" (ngSubmit)="submitPassword()">
                <div>
                  <label for="invite-password" class="block text-sm font-medium text-gray-700">Passwort</label>
                  <input id="invite-password" name="password" type="password" required
                         [attr.autocomplete]="creating ? 'new-password' : 'current-password'"
                         [(ngModel)]="password" [disabled]="busy"
                         [attr.aria-describedby]="creating ? 'invite-password-rules' : null"
                         class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"/>
                  <!-- Stated up front rather than only in the refusal: a policy somebody meets on the first
                       attempt costs nothing, and one they discover by failing costs them the whole form. Only
                       while a password is being SET: telling somebody signing in what their existing password
                       must look like is advice they cannot act on. -->
                  @if (creating) {
                    <p id="invite-password-rules" class="mt-1 text-xs text-gray-500">{{ passwordHint }}</p>
                  }
                </div>

                <!-- Password confirmation (#577), and only in the shape that creates an account. A typo here is
                     unrecoverable in a way it is not on an ordinary sign-in: creating the account with a
                     mistyped password does not fail fast on the next attempt, it silently locks the invitee out
                     of the account they think they just made. Never sent to the server (see redeem): its only
                     job is catching the typo before the account exists at all. Signing in needs none of that,
                     because a wrong password there is simply refused. -->
                @if (creating) {
                  <div>
                    <label for="invite-confirm-password" class="block text-sm font-medium text-gray-700">
                      Passwort bestätigen
                    </label>
                    <input id="invite-confirm-password" name="confirm-password" type="password"
                           autocomplete="new-password" required
                           [(ngModel)]="confirmPassword" [disabled]="busy"
                           class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"/>
                  </div>

                  <!-- Shown only once there is something to compare: an empty confirmation is unfinished, not yet
                       a mismatch, and flagging it before the invitee has even reached the field would just be
                       noise. -->
                  @if (confirmPassword && confirmPassword !== password) {
                    <p class="text-sm text-red-700" role="alert">Die Passwörter stimmen nicht überein.</p>
                  }
                } @else {
                  <!-- The native reset flow, reached with the same kind=account the login screen's own link
                       carries (#279). It is the one form of help that fits here: somebody who has an account and
                       cannot remember its password would otherwise have to abandon the invitation to get back
                       in. The employee reset is deliberately NOT offered, because an employee password opens no
                       account this door accepts.
                       (No backticks anywhere in this template: it is a template literal, style guide gotcha.) -->
                  <div class="flex justify-end">
                    <a [routerLink]="['/selfservice/forgot-password']" [queryParams]="{kind: 'account'}"
                       class="text-sm font-semibold text-theme-primary-700 underline hover:text-theme-primary-900">
                      Passwort vergessen?
                    </a>
                  </div>
                }

                @if (error) {
                  <p class="text-sm text-red-700" role="alert">{{ error }}</p>
                }

                <button type="submit" [disabled]="busy || !canSubmitPassword()"
                        class="w-full rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
                  @if (creating) {
                    Konto anlegen und Einladung annehmen
                  } @else {
                    Anmelden und Einladung annehmen
                  }
                </button>
              </form>
            }

            <!-- Shared with the login screen (#576) — this used to carry its own mt-5 wrapper on top of the row's
                 own vertical padding, which is what made "oder" sit visibly further from both neighbours than on
                 login. mt-6 here plus OrDividerComponent's own py-1 reproduces login's rhythm exactly: there,
                 the same 1.5rem comes from the form's space-y-6 instead. -->
            <auth-or-divider class="mt-6"/>

            <!-- One button per provider rather than a single "Microsoft" action: which account the invitee uses is
                 their decision, and offering only Microsoft would quietly make it for them (mirrors the migration
                 notice, which offers the same three doors when claiming an existing legacy login).

                 Kept on BOTH native steps. Somebody who typed an address and then decided to use their Google
                 account should not have to find their way back to step one, and on a locked invitation there is
                 no way back to find. -->
            <div class="mt-6 space-y-2">
              <button type="button" (click)="accept('microsoft')" [disabled]="busy"
                      class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
                <svg class="h-4 w-4" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Mit Microsoft anmelden und annehmen
              </button>

              <button type="button" (click)="accept('apple')" [disabled]="busy"
                      class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                     aria-hidden="true">
                  <path d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.5zM14.9 5.6c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.06 1.7-.93 2.7.97.08 1.97-.49 2.59-1.23z"/>
                </svg>
                Mit Apple anmelden und annehmen
              </button>

              <button type="button" (click)="accept('google')" [disabled]="busy"
                      class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
                <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.87c2.26-2.09 3.56-5.17 3.56-8.87z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78h-4v3.09A12 12 0 0 0 12 24z"/>
                  <path fill="#FBBC05" d="M5.35 14.31a7.19 7.19 0 0 1 0-4.6v-3.1h-4a12 12 0 0 0 0 10.8l4-3.1z"/>
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.35 6.62l4 3.09A7.2 7.2 0 0 1 12 4.75z"/>
                </svg>
                Mit Google anmelden und annehmen
              </button>
            </div>
          } @else {

            <!-- The mailbox confirmation step (#580). It REPLACES the form and the provider buttons rather than
                 appearing under them: a code has just been mailed and the only useful next act is typing it, so
                 leaving three sign-in doors open beside it would invite somebody to abandon a round trip that is
                 already half done. "Angaben ändern" is the way back, and it is deliberately not a cancel: the
                 typed address and password are kept. -->
            <form class="mt-5 space-y-3" (ngSubmit)="confirmAndRedeem()">
              <div>
                <h2 class="text-sm font-semibold text-gray-900">Bestätigungscode eingeben</h2>
                <!-- One calm line, and nothing else (#586). The enforcement paragraph that used to sit here
                     restated a rule in red at the moment the invitee is already doing the thing it asks for,
                     which reads as a refusal rather than as an instruction. What is genuinely useful at this
                     point is the address the message went to and what to do if it is unreachable, so that is
                     what the line says. A real refusal still appears below, where refusals happen. -->
                <p class="mt-1 text-sm text-gray-600" role="note">
                  Der Code wurde an {{ email }} gesendet. Wenn Sie dieses Postfach nicht lesen können, bitten Sie
                  die einladende Person um eine neue Einladung.
                </p>
              </div>

              <div>
                <label for="invite-confirmation-code" class="block text-sm font-medium text-gray-700">
                  Bestätigungscode
                </label>
                <input id="invite-confirmation-code" name="confirmation-code" type="text"
                       inputmode="numeric" autocomplete="one-time-code" maxlength="6" required
                       [(ngModel)]="confirmationCode" [disabled]="busy"
                       aria-describedby="invite-confirmation-validity"
                       class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-center font-mono text-lg tracking-[0.4em] text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700"/>
                <!-- aria-live, so the countdown reaching zero is announced rather than only redrawn. "polite"
                     because it must never interrupt somebody mid-entry. -->
                <p id="invite-confirmation-validity" class="mt-1 text-xs text-gray-600"
                   role="status" aria-live="polite">
                  @if (confirmationExpired) {
                    Der Code ist abgelaufen. Fordern Sie bitte einen neuen Code an.
                  } @else {
                    Der Code ist noch {{ remainingTime }} gültig.
                  }
                </p>
              </div>

              @if (error) {
                <p class="text-sm text-red-700" role="alert">{{ error }}</p>
              }

              <button type="submit" [disabled]="busy || confirmationExpired || confirmationCode.length !== 6"
                      class="w-full rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
                @if (creating) {
                  Konto anlegen und Einladung annehmen
                } @else {
                  Anmelden und Einladung annehmen
                }
              </button>

              <div class="flex items-center justify-between gap-3">
                <button type="button" (click)="resendConfirmation()" [disabled]="busy"
                        class="text-sm font-semibold text-theme-primary-700 underline disabled:opacity-50">
                  Code erneut senden
                </button>
                <button type="button" (click)="backToForm()" [disabled]="busy"
                        class="text-sm font-semibold text-gray-600 underline disabled:opacity-50">
                  Angaben ändern
                </button>
              </div>
            </form>
          }
        }
      </div>
    </div>
  `
})
export class InviteLandingComponent implements OnInit, OnDestroy {
  protected loading = true;
  protected invalid = false;
  protected busy = false;
  protected invitation: InvitationPreview | null = null;

  /** Which step of the native door is on screen. Starts at the address, which is the only thing it can ask
   *  before the server has told it anything (#583). */
  protected step: InviteNativeStep = 'email';

  /** The e-mail + password option (#279). Prefilled from the binding when there is one — see {@link ngOnInit}. */
  protected email = '';
  protected password = '';

  /**
   * The password confirmation (#577). Compared client-side ONLY — see {@link redeem} — and never sent to the
   * server; its whole job is catching a typo before the account this form creates exists at all. Asked for only
   * when the lookup said the address has no account yet.
   */
  protected confirmPassword = '';

  /** The six digits mailed to the bound address (#580), typed back on {@link step} `confirm`. */
  protected confirmationCode = '';

  /** The refusal of the last native attempt, in German, mapped from the server's code. */
  protected error: string | null = null;

  /** Seconds left on the mailed code, recomputed every second while {@link step} is `confirm`. */
  protected remainingSeconds = 0;

  private code = '';

  /**
   * Whether the typed address already has a native account, as
   * {@link AuthenticationService.lookupInvitationRedemption} answered it (#583). `null` until it has been asked,
   * which is exactly while {@link step} is `email`.
   *
   * It decides the whole of the second step: one password field or two, whether the policy is worth stating, and
   * which verb the button carries. Never inferred from anything the invitee typed — the question is about the
   * server's account store, and guessing it is what the step exists to stop.
   */
  private accountExists: boolean | null = null;

  /**
   * Whether the lookup said this invitation admits no other address.
   *
   * The same fact the preview already carries through `nativeRedeem`, from the endpoint that will actually
   * decide. Recorded so the second step can withhold the way back to a field whose every other value would be
   * refused, even if the preview and the redemption were answered by different builds during a rollout.
   */
  private lookupLocked = false;

  /** When the mailed code stops being accepted, as the server reported it. Null while none is outstanding. */
  private confirmationExpiresAt: string | null = null;

  private countdown: ReturnType<typeof setInterval> | null = null;

  /**
   * Whether a `E_GRANT_INVITE_CONFIRMATION_REQUIRED` refusal has already been answered by starting a
   * confirmation. It exists to bound that recovery to one attempt: the refusal is only reachable through a race
   * (an administrator turning enforcement on between the preview and the submit), and a refusal that kept
   * re-triggering a mail would be a mail loop driven by the server's own answer.
   */
  private recoveredFromLateConfirmation = false;

  constructor(private route: ActivatedRoute, private http: MigrationHttpService,
              private auth: AuthenticationService) {
  }

  ngOnInit(): void {
    this.code = this.route.snapshot.queryParamMap.get('code') ?? '';
    if (!this.code) {
      this.loading = false;
      this.invalid = true;
      return;
    }

    this.http.previewInvitation(this.code).subscribe({
      next: invitation => {
        this.invitation = invitation;
        // A bound invitation already names the address it is for, and the server will refuse any other one.
        // Prefilling it removes the one keystroke that can turn a valid invitation into a refusal — it is not a
        // secret (the page has just displayed it) and it stays editable while the binding is soft, because an
        // unbound invitation legitimately goes to whatever address the invitee wants to use.
        this.email = invitation.boundEmail ?? '';
        this.loading = false;
      },
      error: () => {
        this.invalid = true;
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  // ----- what the invitation asks of this door -----

  /**
   * Which native path this invitation puts the invitee on (#580).
   *
   * An absent value reads as `open`, which is the pre-#580 behaviour exactly: an older backend during a rollout
   * window must degrade to the form this page has always shown rather than locking a field or demanding a code
   * the server is not actually insisting on.
   */
  protected get nativeRedeem(): NativeRedeemMode {
    return this.invitation?.nativeRedeem ?? 'open';
  }

  /**
   * Whether the address is fixed to the bound one. Both enforced modes insist on exactly that address, and the
   * lookup's own `locked` is honoured too so a disagreement between the two builds resolves towards the stricter
   * answer rather than towards an editable field the redemption would refuse.
   */
  protected get emailLocked(): boolean {
    return this.lookupLocked || (!!this.invitation?.boundEmail && this.nativeRedeem !== 'open');
  }

  /**
   * Whether this submission would CREATE an account, which is what decides the confirmation field and the verb.
   *
   * Answered by the lookup, never by an intention the invitee declared. Before the lookup has run there is
   * nothing to create or sign into yet, so `null` reads as false and the second step is simply not on screen.
   */
  protected get creating(): boolean {
    return this.accountExists === false;
  }

  /** Whether the outstanding mailed code has run out. Drives the sentence and disables the submit. */
  protected get confirmationExpired(): boolean {
    return this.step === 'confirm' && this.remainingSeconds <= 0;
  }

  /** The countdown as `m:ss`, which is how somebody reads a few minutes rather than a number of seconds. */
  protected get remainingTime(): string {
    const total = Math.max(0, this.remainingSeconds);
    const minutes = Math.floor(total / 60);
    const seconds = `${total % 60}`.padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  /** Whether the address step has something to look up. */
  protected canContinue(): boolean {
    return !!this.email.trim();
  }

  /**
   * Whether the password step is complete enough to submit.
   *
   * The confirmation is required only while an account is being created — see the field's own doc. Duplicated by
   * the button's `[disabled]` rather than replacing it, because the method also guards every other way of
   * triggering a submit (the Enter key on a field, a story driving the form).
   */
  protected canSubmitPassword(): boolean {
    if (!this.password) return false;
    return !this.creating || (!!this.confirmPassword && this.confirmPassword === this.password);
  }

  // ----- the three ways in -----

  protected accept(provider: LoginProvider): void {
    if (this.busy) return;
    this.busy = true;

    this.auth.redeemInvitation(this.code, this.signingInUrl(true), provider);
  }

  /**
   * The address step's submit: asks whether this address already has an account, then shows the matching
   * password step (#583).
   *
   * Nothing is redeemed here and no mail is sent. A refused lookup — an invitation that has expired or been
   * withdrawn since the preview — is shown as the ordinary invalid-invitation sentence rather than as a fault of
   * the address.
   */
  protected submitEmail(): void {
    if (this.busy || !this.canContinue()) return;
    this.busy = true;
    this.error = null;

    this.auth.lookupInvitationRedemption(this.code, this.email.trim()).subscribe({
      next: lookup => {
        this.busy = false;
        this.accountExists = lookup.exists;
        this.lookupLocked = lookup.locked;
        this.step = 'password';
      },
      error: err => {
        this.busy = false;
        this.error = this.describeRedeemError(err?.error?.code);
      }
    });
  }

  /**
   * The password step's submit. Either redeems straight away, or asks for the mailbox code first.
   *
   * The branch is decided from {@link nativeRedeem}, i.e. from the preview, which is why nobody fills a password
   * in and is only then told there was a step. The server decides again at redeem time regardless.
   */
  protected submitPassword(): void {
    if (this.busy || !this.canSubmitPassword()) return;

    if (this.nativeRedeem === 'confirmationRequired') {
      this.startConfirmation();
      return;
    }

    this.redeem();
  }

  /** The confirm step's submit: the same redemption, now carrying the code that proves the mailbox. */
  protected confirmAndRedeem(): void {
    if (this.busy || this.confirmationExpired || this.confirmationCode.length !== 6) return;
    this.redeem(this.confirmationCode);
  }

  /**
   * Asks for another code. Deliberately the same call as the first request: the server invalidates whatever was
   * outstanding and starts a fresh window, so there is no second endpoint and no "resend" state to get wrong.
   */
  protected resendConfirmation(): void {
    if (this.busy) return;
    this.confirmationCode = '';
    this.startConfirmation();
  }

  /** Back to the password step with everything typed so far intact. Not a cancel: nothing is abandoned. */
  protected backToForm(): void {
    if (this.busy) return;
    this.stopCountdown();
    this.step = 'password';
    this.error = null;
  }

  /**
   * Back to the address step, to try a different one (#583).
   *
   * The password goes with it, unlike everything else this page keeps across a step change. It was typed for
   * the account behind the previous address, and carrying it into a door that may now be the other shape
   * entirely would turn a deliberate change of address into a refusal nobody could explain. The lookup's answer
   * is dropped for the same reason: it described an address that is about to stop being the one in the field.
   */
  protected backToEmail(): void {
    if (this.busy) return;
    this.step = 'email';
    this.accountExists = null;
    this.password = '';
    this.confirmPassword = '';
    this.error = null;
  }

  /**
   * Asks the server to mail a one-time code to the invitation's bound address, and moves to the confirm step.
   *
   * The address is passed so the server can refuse before mailing anything. It never decides WHERE the message
   * goes — that is always the invitation's stored address — so this cannot be used to have a code delivered
   * somewhere of the caller's choosing.
   */
  private startConfirmation(): void {
    this.busy = true;
    this.error = null;

    this.auth.startInvitationEmailConfirmation(this.code, this.email).subscribe({
      next: challenge => {
        this.busy = false;
        this.step = 'confirm';
        this.startCountdown(challenge.expiresAt);
      },
      error: err => {
        this.busy = false;
        this.error = this.describeRedeemError(err?.error?.code);
      }
    });
  }

  /**
   * Accepts the invitation by setting or proving an e-mail address and a password.
   *
   * Unlike {@link accept} this is an XHR, so there is no provider round trip and no handoff: the session comes
   * back in the response. It still lands on the signing-in page, which is the ONE place that decides where a
   * fresh external session goes (enter the single context silently, or ask which of several) — routing straight
   * to the app would be a second copy of that decision, and the copy would be the one that fell behind.
   */
  private redeem(emailConfirmationCode?: string): void {
    this.busy = true;
    this.error = null;

    this.auth.redeemInvitationWithPassword(this.code, this.email, this.password,
      emailConfirmationCode ? {emailConfirmationCode} : {}).subscribe({
      next: () => {
        this.stopCountdown();
        window.open(this.signingInUrl(), '_self');
      },
      error: err => {
        this.busy = false;
        const code = err?.error?.code as string | undefined;

        // The race the server documents: enforcement was turned on between the preview and this submit, so the
        // page offered a form that is now one step short. Recovering by starting the confirmation is strictly
        // better than telling somebody to reload, and it is bounded to one attempt — see the field's doc.
        if (code === 'E_GRANT_INVITE_CONFIRMATION_REQUIRED' && !this.recoveredFromLateConfirmation) {
          this.recoveredFromLateConfirmation = true;
          this.startConfirmation();
          return;
        }

        this.error = this.describeRedeemError(code);
      }
    });
  }

  /**
   * Where a native or federated redemption comes back to.
   *
   * MUST be the signing-in page, not the app root. Redeeming is the one flow that MINTS a session, so the
   * federated callback returns carrying a one-time `handoff` — and `SigningInComponent` is the only place that
   * exchanges one. Pointed at the root, the redeem succeeded server-side (grant created, token minted) and the
   * handoff was dropped on the floor: no session, so `AuthGuard` bounced the invitee to `/login`, having
   * apparently done nothing at all. The same page also renders the `?error=` the callback appends on failure,
   * which the root silently swallowed too — so both outcomes looked identical from the outside.
   *
   * **It carries the invitation code (#582), and that is what makes the failure page recoverable.** Somebody
   * whose redemption is refused has no account, so the signing-in page's "Zurück zur Anmeldung" was a dead end
   * for exactly the population that reaches it through here. With the code along it can offer the way back to
   * the invitation instead, which is still valid.
   *
   * **Carried in the URL rather than in `CookieService`**, and the reason is the provider round trip: the
   * returnUrl is held in server-side flow state (`ExternalSignInFlowController`) and the callback APPENDS to it
   * (`SocialSignInControllerBase.AppendQuery` picks `&` when a query string is already there), so `?invite=` comes
   * back verbatim beside `?handoff=`/`?error=`. `localStorage` would survive the redirect too, but it is trapped
   * behind cookie consent, it would park a live bearer secret with no expiry and no owner, and it would still be
   * sitting there for the next, unrelated sign-in. The code is already in the URL the invitee is reading, so
   * putting it in this one widens nothing.
   *
   * Link and Claim legitimately return to their own page instead: those callers already hold a session and get
   * `?linked=`/`?error=` appended, with no handoff in play.
   *
   * @param absolute whether the result must be an absolute URL, which the backend needs for a redirect target.
   */
  private signingInUrl(absolute = false): string {
    const base = `${absolute ? location.origin : ''}/${AppRoutes.AuthenticationSigningIn}`;
    return `${base}?invite=${encodeURIComponent(this.code)}`;
  }

  // ----- the countdown -----

  private startCountdown(expiresAt: string): void {
    this.stopCountdown();
    this.confirmationExpiresAt = expiresAt;
    this.tick();
    this.countdown = setInterval(() => this.tick(), 1000);
  }

  private stopCountdown(): void {
    if (!this.countdown) return;
    clearInterval(this.countdown);
    this.countdown = null;
  }

  private tick(): void {
    const end = this.confirmationExpiresAt ? new Date(this.confirmationExpiresAt).getTime() : 0;
    this.remainingSeconds = Math.max(0, Math.ceil((end - Date.now()) / 1000));
    // Nothing left to count. The interval is stopped rather than left running on a value that can no longer
    // change, so a page abandoned on this step costs no timer.
    if (this.remainingSeconds === 0) this.stopCountdown();
  }

  // ----- refusals -----

  /**
   * The German sentence for a refused lookup or redemption on this page.
   *
   * Everything falls through to {@link describeNativeAccountError} except the codes below, which that table
   * cannot phrase: it is keyed on native-door codes and knows nothing about *this* invitation, so an unmapped
   * `E_GRANT_INVITE_EMAIL_MISMATCH` degraded to "E-Mail-Adresse oder Passwort ist falsch." — telling somebody to
   * check a password when the actual problem is which address they used.
   *
   * They are named here rather than in `local-account-errors.ts` because the useful half of most of these
   * sentences is the bound address, which only this component holds.
   */
  private describeRedeemError(code: string | null | undefined): string {
    switch (code) {
      case 'E_GRANT_INVITE_EMAIL_MISMATCH':
        return this.describeMismatch();
      case 'E_GRANT_INVITE_CONFIRMATION_REQUIRED':
        return 'Für diese Einladung brauchen wir noch eine Bestätigung Ihrer E-Mail-Adresse. Fordern Sie dazu '
          + 'bitte einen Bestätigungscode an.';
      case 'E_GRANT_INVITE_CONFIRMATION_INVALID':
        return 'Der Bestätigungscode ist falsch oder abgelaufen. Fordern Sie bitte einen neuen Code an.';
      case 'E_GRANT_INVITE_CONFIRMATION_NOT_REQUIRED':
        return 'Für diese Einladung ist kein Bestätigungscode nötig. Laden Sie die Seite bitte neu und nehmen '
          + 'Sie die Einladung direkt an.';
      case 'E_GRANT_INVITE_CONFIRMATION_SEND_FAILED':
        return 'Der Bestätigungscode konnte nicht versendet werden. Bitte versuchen Sie es in einem Moment '
          + 'noch einmal.';
      case 'E_GRANT_INVITE_INVALID':
        return 'Diese Einladung ist abgelaufen, wurde bereits eingelöst oder zurückgezogen. Bitten Sie die '
          + 'einladende Person um eine neue Einladung.';
      default:
        return describeNativeAccountError(code);
    }
  }

  /**
   * The address-mismatch refusal, in the strength the invitation was issued at.
   *
   * The two strengths are separated for the same reason they are on the note above the form: unenforced, using a
   * different address is the whole of the problem; enforced, the remedy may be a new invitation rather than
   * another attempt, and saying so is the only thing that stops somebody retrying the same address forever.
   */
  private describeMismatch(): string {
    const bound = this.invitation?.boundEmail;
    if (!bound) {
      return 'Diese Einladung gehört zu einer anderen E-Mail-Adresse als der, mit der Sie sich anmelden '
        + 'möchten.';
    }

    return this.invitation?.enforceBoundEmail
      ? `Diese Einladung gilt nur für ${bound}. Sie lässt sich nur mit einem Konto einlösen, dessen `
        + 'E-Mail-Adresse genau dieser entspricht und dort bestätigt ist. Bitten Sie die einladende Person um '
        + 'eine neue Einladung, wenn Sie diese Adresse nicht verwenden können.'
      : `Diese Einladung gilt für ${bound}. Melden Sie sich bitte mit dem Konto an, das zu dieser Adresse `
        + 'gehört.';
  }

  // ----- display helpers -----

  /**
   * The full sentence behind a role — the same one the issuing screen shows, deliberately not a second wording
   * maintained here.
   *
   * ### One description, two mechanisms, never both at once
   *
   * A tooltip is hover-driven and a phone has no hover, so the sentence has to arrive some other way below
   * `md`. It used to arrive BOTH ways: the tooltip's `aria-describedby` was attached at every width, and the
   * inline copy was rendered `md:hidden` and marked `aria-hidden="true"` to stop it being read a second time.
   * That kept the count at one only while a Tailwind class and an ARIA attribute agreed by hand, and it bought
   * the agreement by taking the copy a phone user can actually SEE out of the accessibility tree.
   *
   * {@link compact} now decides both, so they cannot disagree:
   *
   *  * **below `md`** the inline text is rendered as ordinary content and `matTooltipDisabled` stops Material
   *    attaching a description at all — the one copy that reaches assistive technology is the one on screen;
   *  * **from `md` up** the inline text is not in the DOM and the tooltip's `aria-describedby` is the only
   *    description, reachable by the pointer and the focus monitor that a desktop actually has.
   *
   * `matTooltipDisabled` rather than stripping the attribute afterwards: `aria-describedby` here belongs to the
   * CDK's `AriaDescriber`, which re-attaches it on the next message or disabled change and would silently undo
   * any hand-written removal. Toggling `disabled` is the supported way to tell it not to describe.
   */
  protected readonly roleDescription = describeRole;

  /** Whether the viewport is below `md` — see {@link roleDescription} for what it decides. */
  protected readonly compact = inject(ViewportService).isCompact;

  /**
   * What a password has to look like, from the one place that says it.
   *
   * This used to be a second copy written into this template. Two screens asking somebody to invent a password
   * must not describe the same server-side rule with two different sets of characters — a hint that omits one
   * accepted symbol turns a valid password into a refusal the reader cannot explain.
   */
  protected readonly passwordHint = PASSWORD_POLICY_HINT;

  /** German labels for the central role names, so the invitee reads a job rather than an enum member. */
  protected roleLabel(role: string): string {
    switch (role) {
      case 'CompanyOwner':
        return 'Firmeninhaber:in — alle Standorte und Einstellungen';
      case 'ShardAdmin':
        return 'Standortverwaltung';
      case 'ShiftLead':
        return 'Schichtleitung — laufende Veranstaltung und Schichtplan';
      case 'Payroll':
        return 'Lohnbuchhaltung';
      case 'Steuerberater':
        return 'Steuerberatung — Kassenunterlagen und Datenexporte';
      case 'Auditor':
        return 'Protokolleinsicht';
      case 'SensitiveDataAccessor':
        return 'Zugriff auf sensible Daten';
      case 'Operator':
        return 'Betriebsleitung — Live-Ansicht';
      default:
        return role;
    }
  }
}
